import { ERROR_INVALID_GROUP_ID } from '@pkg/components/Sections/Groups/data/errorMessages';

import { apiFactory } from '@app/client/apiFactory/api';
import {
    type ApiResult,
    type BaseParams,
    type GroupCanEditResponse,
    type GroupCanModerateResponse,
    type GroupFilterQuery,
    type GroupParam,
    type PostParams,
    type ResourceId,
    type SocialGroupTypeOverride,
} from '@app/client/common/structs';
import { type CreateSocialGroup, type SocialGroup, type UpdateSocialGroup } from '@app/gen';

import { Request } from '../../common/api';
import { links } from '../../links';

type GetGroupParams = BaseParams<GroupFilterQuery> & Required<GroupParam>;

/**
 * GET a single Group. Using the Request.getRequest. Does not include NFT items.
 * TODO: getGroup not defined in schema swagger file only has the patchRequest.
 */
const getGroup = async (params: GetGroupParams): Promise<ApiResult<SocialGroupTypeOverride>> =>
    Request.getRequest<SocialGroupTypeOverride>({
        ...params,
        target: links.pods.single(params.groupId).group(),
    });
/**
 * GET a single Group using the APIClient. Does not include NFT items.
 * If {groupId} is not provided, a reject promise is thrown before the call.
 */
export const callGetGroup = (
    params: BaseParams<GroupFilterQuery> & GroupParam,
    isAuthenticated = true,
): Promise<SocialGroupTypeOverride> =>
    typeof params.groupId === 'undefined'
        ? Promise.reject(new Error(ERROR_INVALID_GROUP_ID))
        : apiFactory()
              .call(getGroup, {
                  ...params,
                  groupId: params.groupId,
                  settings: {
                      ...params.settings,
                      credentials: isAuthenticated ? 'include' : 'omit',
                  },
              })
              .then((result) => {
                  if (result.ok) {
                      return result.value;
                  }
                  const errorMessage =
                      result?.error?.detail && typeof result.error.detail === 'string'
                          ? result.error.detail
                          : `Error getting group ${params.groupId}`;
                  throw new Error(errorMessage);
              });

type PostGroupParams = BaseParams & PostParams<CreateSocialGroup>;

export async function postGroup(params: PostGroupParams): Promise<ApiResult<SocialGroup>> {
    return Request.postRequest<SocialGroup>({
        ...params,
        target: links.pods.collection,
    });
}

export const callPostGroup = (params: PostGroupParams): Promise<SocialGroup> => {
    return apiFactory()
        .call(postGroup, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }

            let cause = '';
            if (typeof result.error == 'string') {
                cause = result.error as string;
            }

            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error creating group`;
            throw new Error(errorMessage, { cause: { name: cause, message: errorMessage } });
        });
};

export type PatchGroupParams = BaseParams & PostParams<UpdateSocialGroup> & { id: ResourceId };

export async function patchGroup(params: PatchGroupParams): Promise<ApiResult<SocialGroup>> {
    return Request.patchRequest<SocialGroup>({
        ...params,
        target: links.pods.single(params.id).group(),
    });
}

export const callPatchGroup = (params: PatchGroupParams): Promise<SocialGroup> => {
    return apiFactory()
        .call(patchGroup, {
            ...params,
            id: params.id,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error updating group`;
            throw new Error(errorMessage);
        });
};

export type DeleteGroupParams = BaseParams & { groupId: ResourceId };

export async function deleteGroup(params: DeleteGroupParams): Promise<ApiResult<SocialGroup>> {
    return Request.deleteRequest<SocialGroup>({
        ...params,
        target: links.pods.single(params.groupId).group(),
    });
}

export const callDeleteGroup = (params: DeleteGroupParams): Promise<SocialGroup> => {
    return apiFactory()
        .call(deleteGroup, {
            ...params,
            id: params.groupId,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error updating group`;
            throw new Error(errorMessage);
        });
};

export type DeleteMemberParams = BaseParams & { groupId: ResourceId; memberId: ResourceId };

export async function deleteMember(params: DeleteMemberParams): Promise<ApiResult<SocialGroup>> {
    return Request.deleteRequest<SocialGroup>({
        ...params,
        target: links.pods.single(params.groupId).deleteMember(params.memberId),
    });
}

export const callDeleteMember = (params: DeleteMemberParams): Promise<SocialGroup> => {
    return apiFactory()
        .call(deleteMember, {
            ...params,
            id: params.groupId,
            memberId: params.memberId,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : 'Error removing user from group';
            throw new Error(errorMessage);
        });
};

export async function getCanEdit(params: Required<GroupParam>): Promise<ApiResult<GroupCanEditResponse>> {
    return Request.getRequest<GroupCanEditResponse>({
        ...params,
        target: links.pods.single(params.groupId).canEdit(),
    });
}

export const callCanEdit = (params: GroupParam): Promise<GroupCanEditResponse> => {
    if (!params.groupId) {
        return Promise.reject(new Error(ERROR_INVALID_GROUP_ID));
    }

    return apiFactory()
        .call(getCanEdit, params as Required<GroupParam>)
        .then((result) => {
            if (result.ok) {
                return result.value;
            }

            throw new Error('Error getting can_edit permission status');
        });
};

export async function getCanModerate(params: Required<GroupParam>): Promise<ApiResult<GroupCanModerateResponse>> {
    return Request.getRequest<GroupCanModerateResponse>({
        ...params,
        target: links.pods.single(params.groupId).canModerate(),
    });
}

export const callCanModerate = (params: GroupParam): Promise<GroupCanModerateResponse> => {
    if (!params.groupId) {
        return Promise.reject(new Error(ERROR_INVALID_GROUP_ID));
    }

    return apiFactory()
        .call(getCanModerate, params as Required<GroupParam>)
        .then((result) => {
            if (result.ok) {
                return result.value;
            }

            throw new Error('Error getting can_moderate permission status');
        });
};
